import { Box, CircularProgress } from "@mui/material";
import { ENDPOINTS, frontEndURL, apiURL } from "config/constants";
import { messaging, requestFirebaseNotificationPermission } from "config/firebase";
import { registerServiceWorker } from "config/serviceWorker";
import { useContextController } from "context";
import { changeLocale, login } from "context/actions";
import { onMessage } from "firebase/messaging";
import { useFetch } from "hooks";
import { Fragment, useCallback, useEffect, useRef } from "react";
import { UserModel } from "types";

interface Data {
  user: UserModel;
}

const SUBSCRIPTION_REFRESH_MS = 30 * 60 * 1000;

async function saveSubscriptionToken(token: string) {
  const res = await fetch(`${apiURL}/subscription`, {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify({ token })
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.message || `Subscription failed (${res.status})`);
  }
}

export default function Loader() {
  const { isLoading, response, callFetch } = useFetch<Data>("GET");
  const [{ userData }, dispatch] = useContextController();
  const onMessageRegistered = useRef(false);
  const pushRegistering = useRef(false);

  const registerPushToken = useCallback(async () => {
    if (pushRegistering.current) return;
    pushRegistering.current = true;
    try {
      const registration = await registerServiceWorker();
      if (!(registration instanceof ServiceWorkerRegistration)) {
        console.error("Service worker registration failed", registration);
        return;
      }
      const token = await requestFirebaseNotificationPermission(registration);
      if (!token) return;
      await saveSubscriptionToken(token);
    } catch (err) {
      console.error(err);
    } finally {
      pushRegistering.current = false;
    }
  }, []);

  useEffect(() => {
    if (onMessageRegistered.current) return;
    onMessageRegistered.current = true;
    onMessage(messaging, function (payload) {
      const notificationTitle = payload?.notification?.title;
      const notificationOptions = {
        body: payload?.notification?.body,
        icon: frontEndURL + "/logo192.png"
      };
      const alertId = payload?.data?.alertId;

      if ("Notification" in window && Notification.permission === "granted") {
        const notification = new Notification(notificationTitle || "Unknown Notification", notificationOptions);
        notification.addEventListener("click", function () {
          window.open(frontEndURL + "/threats/" + alertId, "_blank");
          notification.close();
        });
      }
    });
  }, []);

  useEffect(() => {
    if (response?.data?.user) {
      login(response.data?.user, dispatch);
    }
  }, [response, dispatch]);

  useEffect(() => {
    if (!userData) return;
    registerPushToken();
    const intervalId = window.setInterval(registerPushToken, SUBSCRIPTION_REFRESH_MS);
    return () => window.clearInterval(intervalId);
  }, [userData, registerPushToken]);

  useEffect(() => {
    const locale = localStorage.getItem("locale");
    changeLocale(locale === "fr" ? "fr" : "en", dispatch);
    const userData = localStorage.getItem("userData");
    if (userData) {
      login(JSON.parse(userData) as UserModel, dispatch);
      callFetch(ENDPOINTS.account).catch(console.error);
    }
  }, [callFetch, dispatch]);

  return isLoading ? (
    <Box
      position="fixed"
      top={0}
      left={0}
      zIndex={9999999}
      width="100vw"
      height="100vh"
      bgcolor="rgba(255,255,255,0.5)"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <CircularProgress />
    </Box>
  ) : (
    <Fragment></Fragment>
  );
}
